import axios from "@/http";
import store from "@/store";
import {
  resolveOrderPath,
  OWNED_MALL_ORDER_PATH_GROUP,
} from "@/utils/orderPathFilter";
import {
  fetchOrdersByDateRange,
  fetchRefundsByDateRange,
  fetchCancelsByDateRange,
  fetchExchangesByDateRange,
  fetchReturnsByDateRange,
  fetchOrdersByIdsChunked,
} from "@/services/settlementApi";
import {
  getOrdersByIdsFromDb,
  saveOrderReferences,
} from "@/services/settlementCache";
import { sanitizeOrderNonPII } from "@/services/dataTransformer";

import moment from "moment";
import {
  getOrderPaymentSummary,
  OrderPaymentSummary,
} from "@/utils/orderPaymentSummary";

export type OrderChannelFilter = "cafe24" | "external" | "all";

export interface ClaimOrderRow {
  order_id: string;
  order_date: string;
  payment_date: string;
  channel: string;
  isCafe24: boolean;
  claimTypes: string[];
  claimTypesText: string;
  payment_method_name: string;
  initialPaymentAmount: number;
  actualPaymentAmount: number;
  reducedPaymentAmount: number;
  initialOrderCompositionAmount: number;
  actualOrderCompositionAmount: number;
  reducedOrderCompositionAmount: number;
  itemsSummary: string;
  paymentSummary: OrderPaymentSummary;
  rawOrder: any;
}

export interface ClaimOrdersSummary {
  orderCount: number;
  initialPaymentAmount: number;
  actualPaymentAmount: number;
  reducedPaymentAmount: number;
  initialOrderCompositionAmount: number;
  actualOrderCompositionAmount: number;
  reducedOrderCompositionAmount: number;
}

export interface ClaimOrdersInspectionResult {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: ClaimOrdersSummary;
  byClaimType: {
    cancels: ClaimOrdersSummary;
    orderCancels: ClaimOrdersSummary;
    exchanges: ClaimOrdersSummary;
    returns: ClaimOrdersSummary;
  };
  byChannel: {
    cafe24: ClaimOrdersSummary;
    external: ClaimOrdersSummary;
  };
  orders: ClaimOrderRow[];
}

export interface InspectClaimOrdersOptions {
  date?: string;
  startDate?: string;
  endDate?: string;
  channel?: OrderChannelFilter;
}

export interface ClaimInspectionData {
  startDate: string;
  endDate: string;
  fetchedAt: string;
  orders: any[];
  refunds: any[];
  cancels: any[];
  orderCancels: any[];
  exchanges: any[];
  returns: any[];
}

export function isCafe24Channel(item: any): boolean {
  const resolved = resolveOrderPath(item, "grouped");
  return resolved.key === `group:${OWNED_MALL_ORDER_PATH_GROUP.key}`;
}

export function isExternalChannel(item: any): boolean {
  return !isCafe24Channel(item);
}

export function filterItemsByChannel(
  items: any[],
  channel: OrderChannelFilter = "all"
): any[] {
  if (!Array.isArray(items)) return [];
  if (channel === "cafe24") {
    return items.filter(isCafe24Channel);
  }
  if (channel === "external") {
    return items.filter(isExternalChannel);
  }
  return items;
}

function getItemAmount(item: any): number {
  const amount =
    item?.actual_refund_amount ??
    item?.refund_amount ??
    item?.actual_order_amount?.order_price_amount ??
    item?.payment_amount ??
    item?.total_amount ??
    0;
  const num = Number(amount);
  return Number.isFinite(num) ? num : 0;
}

export class ClaimInspector {
  public data: ClaimInspectionData = {
    startDate: "",
    endDate: "",
    fetchedAt: "",
    orders: [],
    refunds: [],
    cancels: [],
    orderCancels: [],
    exchanges: [],
    returns: [],
  };

  private getContext(): { mallId: string; shopNo: string } {
    const mallId =
      store.state.mall_info?.mall_id || store.state.shop?.mall_id || "";
    const shopNo = String(
      store.state.mall_info?.shop_no ?? store.state.shop?.shop_no ?? "1"
    );
    return { mallId, shopNo };
  }

  /**
   * Enriches refund items with their referenced order details if not already present,
   * utilizing Dexie cache and /query/orders/by-ids.
   */
  private async enrichRefundsWithOrders(
    refunds: any[],
    orderMap: Map<string, any>
  ): Promise<void> {
    if (!Array.isArray(refunds) || refunds.length === 0) return;

    const { mallId, shopNo } = this.getContext();
    const notFoundOrderIds = Array.from(
      new Set(
        refunds
          .map((item) => String(item?.order_id || "").trim())
          .filter((orderId) => orderId && !orderMap.has(orderId))
      )
    );

    if (notFoundOrderIds.length > 0 && mallId) {
      // 1. Check local IndexedDB cache
      const cached = await getOrdersByIdsFromDb(
        mallId,
        shopNo,
        notFoundOrderIds
      );
      cached.forEach((order) => {
        const id = String(order?.order_id || "").trim();
        if (id) orderMap.set(id, order);
      });

      // 2. Fetch still missing orders from API
      const missing = notFoundOrderIds.filter((id) => !orderMap.has(id));
      if (missing.length > 0) {
        console.log(
          `[ClaimInspector] Fetching ${missing.length} missing reference orders for refunds...`
        );
        const fetched = await fetchOrdersByIdsChunked(axios, missing, 50, 600);
        fetched.forEach((order) => {
          const id = String(order?.order_id || "").trim();
          if (id) orderMap.set(id, order);
        });

        // 3. Cache fetched orders in IndexedDB for future runs
        await saveOrderReferences(
          mallId,
          shopNo,
          fetched.map((o) => sanitizeOrderNonPII(o))
        );
      }
    }

    // 4. Link order object to refund & fill missing order_place fields
    refunds.forEach((item) => {
      const orderId = String(item?.order_id || "").trim();
      if (orderId && orderMap.has(orderId)) {
        const order = orderMap.get(orderId);
        item.order = order;
        if (!item.order_place_id && order?.order_place_id) {
          item.order_place_id = order.order_place_id;
        }
        if (!item.order_place_name && order?.order_place_name) {
          item.order_place_name = order.order_place_name;
        }
      }
    });
  }

  /**
   * Fetches all 5 datasets (orders, refunds, cancels, exchanges, returns)
   * for the given date range and caches data.
   */
  public async fetchAll(params: {
    startDate: string;
    endDate: string;
  }): Promise<ClaimInspectionData> {
    const { startDate, endDate } = params;
    if (!startDate || !endDate) {
      throw new Error("startDate와 endDate(YYYY-MM-DD)를 지정해야 합니다.");
    }

    console.log(
      `%c[ClaimInspector] Fetching all claim datasets for ${startDate} ~ ${endDate}...`,
      "color: #0070f3; font-weight: bold;"
    );

    const [orders, refunds, cancels, orderCancels, exchanges, returns] =
      await Promise.all([
        fetchOrdersByDateRange(axios, startDate, endDate),
        fetchRefundsByDateRange(axios, startDate, endDate),
        fetchCancelsByDateRange(
          axios,
          startDate,
          endDate,
          "cancel_complete_date"
        ),
        fetchCancelsByDateRange(axios, startDate, endDate, "cancel_date"),
        fetchExchangesByDateRange(axios, startDate, endDate),
        fetchReturnsByDateRange(axios, startDate, endDate),
      ]);

    // Build base order map from all fetched orders
    const orderMap = new Map<string, any>();
    const registerOrders = (list: any[]) => {
      if (!Array.isArray(list)) return;
      list.forEach((o) => {
        const id = String(o?.order_id || "").trim();
        if (id && !orderMap.has(id)) orderMap.set(id, o);
      });
    };
    registerOrders(orders);
    registerOrders(cancels);
    registerOrders(orderCancels);
    registerOrders(exchanges);
    registerOrders(returns);

    // Only refunds need to be enriched with their referenced Order object
    if (refunds.length > 0) {
      console.log(
        "[ClaimInspector] Enriching refunds with referenced orders..."
      );
      await this.enrichRefundsWithOrders(refunds, orderMap);
    }

    this.data = {
      startDate,
      endDate,
      fetchedAt: new Date().toISOString(),
      orders,
      refunds,
      cancels,
      orderCancels,
      exchanges,
      returns,
    };

    console.log(
      `%c[ClaimInspector] Data collection complete!`,
      "color: #00c642; font-weight: bold;"
    );
    this.summary();
    return this.data;
  }

  public async fetchCancels(
    startDate: string,
    endDate: string,
    dateType = "cancel_complete_date"
  ): Promise<any[]> {
    const cancels = await fetchCancelsByDateRange(
      axios,
      startDate,
      endDate,
      dateType
    );
    if (dateType === "cancel_date") {
      this.data.orderCancels = cancels;
      console.log(
        `[ClaimInspector] OrderCancels(cancel_date) loaded: ${cancels.length} items`
      );
    } else {
      this.data.cancels = cancels;
      console.log(
        `[ClaimInspector] Cancels(cancel_complete_date) loaded: ${cancels.length} items`
      );
    }
    return cancels;
  }

  public async fetchOrderCancels(
    startDate: string,
    endDate: string
  ): Promise<any[]> {
    return this.fetchCancels(startDate, endDate, "cancel_date");
  }

  public async fetchExchanges(
    startDate: string,
    endDate: string
  ): Promise<any[]> {
    const exchanges = await fetchExchangesByDateRange(
      axios,
      startDate,
      endDate
    );
    this.data.exchanges = exchanges;
    console.log(`[ClaimInspector] Exchanges loaded: ${exchanges.length} items`);
    return exchanges;
  }

  public async fetchReturns(
    startDate: string,
    endDate: string
  ): Promise<any[]> {
    const returns = await fetchReturnsByDateRange(axios, startDate, endDate);
    this.data.returns = returns;
    console.log(`[ClaimInspector] Returns loaded: ${returns.length} items`);
    return returns;
  }

  public async fetchOrders(startDate: string, endDate: string): Promise<any[]> {
    const orders = await fetchOrdersByDateRange(axios, startDate, endDate);
    this.data.orders = orders;
    console.log(`[ClaimInspector] Orders loaded: ${orders.length} items`);
    return orders;
  }

  public async fetchRefunds(
    startDate: string,
    endDate: string
  ): Promise<any[]> {
    const refunds = await fetchRefundsByDateRange(axios, startDate, endDate);
    const orderMap = new Map<string, any>(
      this.data.orders.map((o) => [String(o?.order_id || "").trim(), o])
    );
    await this.enrichRefundsWithOrders(refunds, orderMap);
    this.data.refunds = refunds;
    console.log(`[ClaimInspector] Refunds loaded: ${refunds.length} items`);
    return refunds;
  }

  public getCancels(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.cancels, channel);
  }

  public getOrderCancels(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.orderCancels, channel);
  }

  public getExchanges(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.exchanges, channel);
  }

  public getReturns(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.returns, channel);
  }

  public getOrders(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.orders, channel);
  }

  public getRefunds(channel: OrderChannelFilter = "all"): any[] {
    return filterItemsByChannel(this.data.refunds, channel);
  }

  /**
   * Prints structured summary tables to the console.
   */
  public summary(): void {
    const categories = [
      { name: "주문 (orders)", key: "orders", items: this.data.orders },
      { name: "환불 (refunds)", key: "refunds", items: this.data.refunds },
      {
        name: "취소완료 (cancels - cancel_complete_date)",
        key: "cancels",
        items: this.data.cancels,
      },
      {
        name: "주문취소 (orderCancels - cancel_date)",
        key: "orderCancels",
        items: this.data.orderCancels,
      },
      {
        name: "교환완료 (exchanges)",
        key: "exchanges",
        items: this.data.exchanges,
      },
      { name: "반품완료 (returns)", key: "returns", items: this.data.returns },
    ];

    const overviewTable = categories.map((cat) => {
      const total = cat.items.length;
      const cafe24Items = cat.items.filter(isCafe24Channel);
      const externalItems = cat.items.filter(isExternalChannel);
      const totalAmount = cat.items.reduce(
        (sum, item) => sum + getItemAmount(item),
        0
      );
      const cafe24Amount = cafe24Items.reduce(
        (sum, item) => sum + getItemAmount(item),
        0
      );
      const externalAmount = externalItems.reduce(
        (sum, item) => sum + getItemAmount(item),
        0
      );

      return {
        데이터셋: cat.name,
        전체건수: total,
        "자사몰 건수 (self·mobile)": cafe24Items.length,
        "외부채널 건수 (NPay·Coupang 등)": externalItems.length,
        "전체 합계금액": Math.round(totalAmount).toLocaleString() + "원",
        "자사몰 금액": Math.round(cafe24Amount).toLocaleString() + "원",
        "외부채널 금액": Math.round(externalAmount).toLocaleString() + "원",
      };
    });

    console.group(
      `%c[ClaimInspector Summary] Period: ${this.data.startDate || "N/A"} ~ ${
        this.data.endDate || "N/A"
      }`,
      "color: #0070f3; font-weight: bold;"
    );
    console.log("%c1. 데이터셋별 전체 및 채널 분할 요약", "font-weight: bold;");
    console.table(overviewTable);

    // Detailed place breakdown table
    const places = new Map<
      string,
      {
        label: string;
        orders: number;
        refunds: number;
        cancels: number;
        orderCancels: number;
        exchanges: number;
        returns: number;
      }
    >();

    const recordPlace = (
      item: any,
      type:
        | "orders"
        | "refunds"
        | "cancels"
        | "orderCancels"
        | "exchanges"
        | "returns"
    ) => {
      const resolved = resolveOrderPath(item, "grouped");
      const key = resolved.key;
      if (!places.has(key)) {
        places.set(key, {
          label: resolved.label,
          orders: 0,
          refunds: 0,
          cancels: 0,
          orderCancels: 0,
          exchanges: 0,
          returns: 0,
        });
      }
      const entry = places.get(key);
      if (entry) {
        entry[type] += 1;
      }
    };

    this.data.orders.forEach((i) => recordPlace(i, "orders"));
    this.data.refunds.forEach((i) => recordPlace(i, "refunds"));
    this.data.cancels.forEach((i) => recordPlace(i, "cancels"));
    this.data.orderCancels.forEach((i) => recordPlace(i, "orderCancels"));
    this.data.exchanges.forEach((i) => recordPlace(i, "exchanges"));
    this.data.returns.forEach((i) => recordPlace(i, "returns"));

    const placeTable = Array.from(places.values()).map((p) => ({
      주문경로: p.label,
      "주문 (orders)": p.orders,
      "환불 (refunds)": p.refunds,
      "취소완료 (cancels)": p.cancels,
      "주문취소 (orderCancels)": p.orderCancels,
      "교환완료 (exchanges)": p.exchanges,
      "반품완료 (returns)": p.returns,
    }));

    console.log(
      "%c2. 주문경로별(자사몰/마켓별) 상세 건수 분포",
      "font-weight: bold;"
    );
    console.table(placeTable);
    console.groupEnd();
  }

  /**
   * 해당 일자(또는 기간)에 취소 완료 / 교환 완료 / 반품 완료된 주문만 모아서
   * 현재 결제 합계 및 감소한 매출(차감액)을 집계하고 콘솔에 표 형태로 출력합니다.
   */
  public async inspectClaimOrders(
    dateOrOptions?: string | InspectClaimOrdersOptions,
    endDateParam?: string
  ): Promise<ClaimOrdersInspectionResult> {
    let startDate = "";
    let endDate = "";
    let channel: OrderChannelFilter = "all";

    if (typeof dateOrOptions === "object" && dateOrOptions !== null) {
      startDate = dateOrOptions.date || dateOrOptions.startDate || "";
      endDate =
        dateOrOptions.endDate ||
        dateOrOptions.date ||
        dateOrOptions.startDate ||
        "";
      if (dateOrOptions.channel) channel = dateOrOptions.channel;
    } else if (typeof dateOrOptions === "string" && dateOrOptions.trim()) {
      startDate = dateOrOptions.trim();
      endDate =
        typeof endDateParam === "string" && endDateParam.trim()
          ? endDateParam.trim()
          : startDate;
    } else {
      if (
        this.data.startDate &&
        (this.data.cancels.length > 0 ||
          this.data.exchanges.length > 0 ||
          this.data.returns.length > 0)
      ) {
        startDate = this.data.startDate;
        endDate = this.data.endDate || startDate;
        console.log(
          `[ClaimInspector] 기존 로드된 데이터 기간(${startDate} ~ ${endDate})을 기준으로 집계합니다.`
        );
      } else {
        startDate = moment().format("YYYY-MM-DD");
        endDate = startDate;
        console.log(
          `[ClaimInspector] 일자가 지정되지 않아 오늘(${startDate}) 기준으로 조회합니다.`
        );
      }
    }

    let cancels: any[] = [];
    let orderCancels: any[] = [];
    let exchanges: any[] = [];
    let returns: any[] = [];

    const isAlreadyLoaded =
      this.data.startDate === startDate &&
      this.data.endDate === endDate &&
      (this.data.cancels.length > 0 ||
        this.data.orderCancels.length > 0 ||
        this.data.exchanges.length > 0 ||
        this.data.returns.length > 0);

    if (isAlreadyLoaded) {
      cancels = this.data.cancels;
      orderCancels = this.data.orderCancels;
      exchanges = this.data.exchanges;
      returns = this.data.returns;
    } else {
      console.log(
        `%c[ClaimInspector] ${
          startDate === endDate ? startDate : `${startDate} ~ ${endDate}`
        } 일자의 취소·교환·반품 주문 데이터를 조회하는 중...`,
        "color: #0070f3; font-weight: bold;"
      );
      [cancels, orderCancels, exchanges, returns] = await Promise.all([
        fetchCancelsByDateRange(
          axios,
          startDate,
          endDate,
          "cancel_complete_date"
        ),
        fetchCancelsByDateRange(axios, startDate, endDate, "cancel_date"),
        fetchExchangesByDateRange(axios, startDate, endDate),
        fetchReturnsByDateRange(axios, startDate, endDate),
      ]);
      this.data.startDate = startDate;
      this.data.endDate = endDate;
      this.data.fetchedAt = new Date().toISOString();
      this.data.cancels = cancels;
      this.data.orderCancels = orderCancels;
      this.data.exchanges = exchanges;
      this.data.returns = returns;
    }

    // 주문번호(order_id) 기준으로 중복 제거 및 클레임 유형 병합
    const orderMap = new Map<
      string,
      { rawOrder: any; claimTypes: Set<string> }
    >();

    const register = (items: any[], type: string) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        const orderId = String(item?.order_id || "").trim();
        if (!orderId) return;
        let entry = orderMap.get(orderId);
        if (!entry) {
          entry = {
            rawOrder: item,
            claimTypes: new Set<string>(),
          };
          orderMap.set(orderId, entry);
        }
        entry.claimTypes.add(type);
      });
    };

    register(cancels, "취소완료");
    register(orderCancels, "주문취소");
    register(exchanges, "교환완료");
    register(returns, "반품완료");

    const orderRows: ClaimOrderRow[] = [];

    for (const [orderId, { rawOrder, claimTypes }] of orderMap.entries()) {
      const isCafe24 = isCafe24Channel(rawOrder);
      if (channel === "cafe24" && !isCafe24) continue;
      if (channel === "external" && isCafe24) continue;

      const paymentSummary = getOrderPaymentSummary(rawOrder);
      const initialPaymentAmount = paymentSummary.initialPaymentAmount;
      const actualPaymentAmount = paymentSummary.actualPaymentAmount;
      const reducedPaymentAmount = Math.max(
        0,
        initialPaymentAmount - actualPaymentAmount
      );

      const initialOrderCompositionAmount =
        paymentSummary.initialOrderCompositionAmount;
      const actualOrderCompositionAmount =
        paymentSummary.actualOrderCompositionAmount;
      const reducedOrderCompositionAmount = Math.max(
        0,
        initialOrderCompositionAmount - actualOrderCompositionAmount
      );

      const resolved = resolveOrderPath(rawOrder, "grouped");
      const channelLabel = resolved.label;

      const rawItems = Array.isArray(rawOrder?.items) ? rawOrder.items : [];
      const itemsSummary = rawItems
        .map(
          (it: any) =>
            `${it?.product_name || "상품"}(x${it?.quantity || 1}${
              it?.order_status ? `, ${it.order_status}` : ""
            })`
        )
        .join("; ");

      const claimTypesArr = Array.from(claimTypes);

      orderRows.push({
        order_id: orderId,
        order_date: String(rawOrder?.order_date || "")
          .slice(0, 19)
          .replace("T", " "),
        payment_date: String(rawOrder?.payment_date || "")
          .slice(0, 19)
          .replace("T", " "),
        channel: channelLabel,
        isCafe24,
        claimTypes: claimTypesArr,
        claimTypesText: claimTypesArr.join(", "),
        payment_method_name: String(
          rawOrder?.payment_method_name || rawOrder?.payment_method || "N/A"
        ),
        initialPaymentAmount,
        actualPaymentAmount,
        reducedPaymentAmount,
        initialOrderCompositionAmount,
        actualOrderCompositionAmount,
        reducedOrderCompositionAmount,
        itemsSummary,
        paymentSummary,
        rawOrder,
      });
    }

    const summarizeRows = (rows: ClaimOrderRow[]): ClaimOrdersSummary => {
      return rows.reduce(
        (acc, r) => {
          acc.orderCount += 1;
          acc.initialPaymentAmount += r.initialPaymentAmount;
          acc.actualPaymentAmount += r.actualPaymentAmount;
          acc.reducedPaymentAmount += r.reducedPaymentAmount;
          acc.initialOrderCompositionAmount += r.initialOrderCompositionAmount;
          acc.actualOrderCompositionAmount += r.actualOrderCompositionAmount;
          acc.reducedOrderCompositionAmount += r.reducedOrderCompositionAmount;
          return acc;
        },
        {
          orderCount: 0,
          initialPaymentAmount: 0,
          actualPaymentAmount: 0,
          reducedPaymentAmount: 0,
          initialOrderCompositionAmount: 0,
          actualOrderCompositionAmount: 0,
          reducedOrderCompositionAmount: 0,
        }
      );
    };

    const totalSummary = summarizeRows(orderRows);
    const cancelRows = orderRows.filter((r) =>
      r.claimTypes.includes("취소완료")
    );
    const orderCancelRows = orderRows.filter((r) =>
      r.claimTypes.includes("주문취소")
    );
    const exchangeRows = orderRows.filter((r) =>
      r.claimTypes.includes("교환완료")
    );
    const returnRows = orderRows.filter((r) =>
      r.claimTypes.includes("반품완료")
    );
    const cafe24Rows = orderRows.filter((r) => r.isCafe24);
    const externalRows = orderRows.filter((r) => !r.isCafe24);

    const dateLabel =
      startDate === endDate ? startDate : `${startDate} ~ ${endDate}`;

    console.group(
      `%c[ClaimOrders] ${dateLabel} 취소·교환·반품 완료 주문 집계 결과`,
      "color: #0070f3; font-size: 13px; font-weight: bold;"
    );

    console.log(
      `%c🎯 고유 주문수: %c${totalSummary.orderCount.toLocaleString()}건 %c| 💰 현재 결제 합계: %c${Math.round(
        totalSummary.actualPaymentAmount
      ).toLocaleString()}원 %c| 📉 감소한 매출(실결제): %c${Math.round(
        totalSummary.reducedPaymentAmount
      ).toLocaleString()}원`,
      "color: #555;",
      "color: #0070f3; font-weight: bold;",
      "color: #555;",
      "color: #2b8a3e; font-weight: bold;",
      "color: #555;",
      "color: #e03131; font-weight: bold;"
    );

    console.log(
      "\n%c1. 핵심 집계 요약 (전체 합계)",
      "font-weight: bold; color: #111;"
    );
    console.table([
      {
        기준: "실결제금액 (PG/결제수단 승인 기준)",
        "대상 주문수": `${totalSummary.orderCount.toLocaleString()}건`,
        "최초 결제 합계": `${Math.round(
          totalSummary.initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제 합계": `${Math.round(
          totalSummary.actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출 (차감액)": `${Math.round(
          totalSummary.reducedPaymentAmount
        ).toLocaleString()}원`,
      },
      {
        기준: "결제구성액 (상품+배송−할인−쿠폰)",
        "대상 주문수": `${totalSummary.orderCount.toLocaleString()}건`,
        "최초 결제 합계": `${Math.round(
          totalSummary.initialOrderCompositionAmount
        ).toLocaleString()}원`,
        "현재 결제 합계": `${Math.round(
          totalSummary.actualOrderCompositionAmount
        ).toLocaleString()}원`,
        "감소한 매출 (차감액)": `${Math.round(
          totalSummary.reducedOrderCompositionAmount
        ).toLocaleString()}원`,
      },
    ]);

    console.log(
      "\n%c2. 클레임 유형별 요약 (취소완료 / 주문취소 / 교환완료 / 반품완료)",
      "font-weight: bold; color: #111;"
    );
    console.table([
      {
        클레임구분: "취소완료 포함 주문 (cancel_complete_date)",
        주문건수: `${cancelRows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(cancelRows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(cancelRows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(cancelRows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
      {
        클레임구분: "주문취소 포함 주문 (cancel_date)",
        주문건수: `${orderCancelRows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(orderCancelRows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(orderCancelRows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(orderCancelRows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
      {
        클레임구분: "교환완료 포함 주문",
        주문건수: `${exchangeRows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(exchangeRows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(exchangeRows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(exchangeRows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
      {
        클레임구분: "반품완료 포함 주문",
        주문건수: `${returnRows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(returnRows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(returnRows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(returnRows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
    ]);

    console.log(
      "\n%c3. 채널별(자사몰 vs 외부채널) 요약",
      "font-weight: bold; color: #111;"
    );
    console.table([
      {
        채널구분: "카페24 자사몰 (PC·모바일)",
        주문건수: `${cafe24Rows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(cafe24Rows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(cafe24Rows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(cafe24Rows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
      {
        채널구분: "외부채널 (NPay·스마트스토어·쿠팡 등)",
        주문건수: `${externalRows.length.toLocaleString()}건`,
        "최초 결제합계": `${Math.round(
          summarizeRows(externalRows).initialPaymentAmount
        ).toLocaleString()}원`,
        "현재 결제합계": `${Math.round(
          summarizeRows(externalRows).actualPaymentAmount
        ).toLocaleString()}원`,
        "감소한 매출": `${Math.round(
          summarizeRows(externalRows).reducedPaymentAmount
        ).toLocaleString()}원`,
      },
    ]);

    if (orderRows.length > 0) {
      console.log(
        `\n%c4. 개별 주문 상세 내역 (총 ${orderRows.length}건)`,
        "font-weight: bold; color: #111;"
      );
      console.table(
        orderRows.map((r) => ({
          주문번호: r.order_id,
          주문일시: r.order_date,
          주문경로: r.channel,
          클레임구분: r.claimTypesText,
          결제수단: r.payment_method_name,
          최초결제액: `${Math.round(
            r.initialPaymentAmount
          ).toLocaleString()}원`,
          현재결제액: `${Math.round(r.actualPaymentAmount).toLocaleString()}원`,
          감소한매출: `${Math.round(
            r.reducedPaymentAmount
          ).toLocaleString()}원`,
        }))
      );
    } else {
      console.log(
        "%c해당 일자에는 완료된 취소·교환·반품 주문이 없습니다.",
        "color: #888; font-style: italic;"
      );
    }

    console.groupEnd();

    return {
      period: { startDate, endDate },
      summary: totalSummary,
      byClaimType: {
        cancels: summarizeRows(cancelRows),
        orderCancels: summarizeRows(orderCancelRows),
        exchanges: summarizeRows(exchangeRows),
        returns: summarizeRows(returnRows),
      },
      byChannel: {
        cafe24: summarizeRows(cafe24Rows),
        external: summarizeRows(externalRows),
      },
      orders: orderRows,
    };
  }

  /**
   * Prints usage instructions in console.
   */
  public help(): void {
    console.log(
      `
%c=== Claim & Order Inspector 사용 안내 (DevTools 콘솔) ===%c

1. 특정 일자 취소·교환·반품 주문 집계 (현재 결제 합계 및 감소 매출):
   %cawait inspectClaimOrders('2026-09-08')%c
   %cawait inspectClaimOrders('2026-09-01', '2026-09-08')%c
   %cawait claimInspector.inspectClaimOrders('2026-09-08')%c

2. 전체 데이터 조회 (orders, refunds, cancels[취소완료], orderCancels[주문취소], exchanges, returns):
   %cawait claimInspector.fetchAll({ startDate: '2026-09-01', endDate: '2026-09-09' })%c

3. 개별 데이터 조회:
   %cawait claimInspector.fetchCancels('2026-09-01', '2026-09-09')%c       // 취소완료일(cancel_complete_date) 기준
   %cawait claimInspector.fetchOrderCancels('2026-09-01', '2026-09-09')%c  // 주문취소일(cancel_date) 기준
   %cawait claimInspector.fetchExchanges('2026-09-01', '2026-09-09')%c     // 교환완료일 기준
   %cawait claimInspector.fetchReturns('2026-09-01', '2026-09-09')%c       // 반품완료일 기준

4. 주문경로별 필터링 (cafe24 자사몰 vs external 외부채널):
   - 취소완료 (cancel_complete_date):
     %cclaimInspector.getCancels('cafe24')%c    // 자사몰 PC(self)·모바일(mobile) 취소완료건
     %cclaimInspector.getCancels('external')%c  // NPay·쿠팡 등 외부채널 취소완료건
     %cclaimInspector.getCancels()%c            // 전체 취소완료건
   - 주문취소 (cancel_date):
     %cclaimInspector.getOrderCancels('cafe24')%c   // 자사몰 주문취소건
     %cclaimInspector.getOrderCancels('external')%c // 외부채널 주문취소건
     %cclaimInspector.getOrderCancels()%c           // 전체 주문취소건
   - 교환완료:
     %cclaimInspector.getExchanges('external')%c
   - 반품완료:
     %cclaimInspector.getReturns('external')%c
   - 주문/환불:
     %cclaimInspector.getOrders('external')%c
     %cclaimInspector.getRefunds('external')%c

5. 원본 데이터 직접 접근:
   %cclaimInspector.data.cancels%c      // 취소완료 목록 (cancel_complete_date)
   %cclaimInspector.data.orderCancels%c // 주문취소 목록 (cancel_date)
   %cclaimInspector.data.exchanges%c    // 교환완료 목록
   %cclaimInspector.data.returns%c      // 반품완료 목록

6. 요약 테이블 다시 보기:
   %cclaimInspector.summary()%c
`,
      "color: #0070f3; font-size: 14px; font-weight: bold;",
      "color: inherit; font-size: 12px;",
      "color: #2b8a3e; font-family: monospace; font-weight: bold;",
      "color: inherit;",
      "color: #2b8a3e; font-family: monospace; font-weight: bold;",
      "color: inherit;",
      "color: #2b8a3e; font-family: monospace; font-weight: bold;",
      "color: inherit;",
      "color: #d63384; font-family: monospace; font-weight: bold;",
      "color: inherit;",
      "color: #d63384; font-family: monospace;",
      "color: inherit;",
      "color: #d63384; font-family: monospace;",
      "color: inherit;",
      "color: #d63384; font-family: monospace;",
      "color: inherit;",
      "color: #d63384; font-family: monospace;",
      "color: inherit;",
      "color: #2b8a3e; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #868e96; font-family: monospace;",
      "color: inherit;",
      "color: #2b8a3e; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #868e96; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #e67700; font-family: monospace;",
      "color: inherit;",
      "color: #1c7ed6; font-family: monospace;",
      "color: inherit;",
      "color: #1c7ed6; font-family: monospace;",
      "color: inherit;",
      "color: #1c7ed6; font-family: monospace;",
      "color: inherit;",
      "color: #1c7ed6; font-family: monospace;",
      "color: #7048e8; font-family: monospace; font-weight: bold;",
      "color: inherit;"
    );
  }
}

export const claimInspector = new ClaimInspector();
export default claimInspector;
